import { useEffect, useRef, useState } from 'react';
import type { CustomerListItem, MeResponse, ProductListItem } from '@kassa/shared';
import { api } from '../api';
import { hasPermission } from '../format';
import type { NavSection } from '../nav';
import { searchNorm } from '../../../../packages/shared/src/search';

// Ctrl+K qlobal axtarış (KT-108): menyu bəndləri, mallar (ad, barkod, kod), müştərilər — ə/ş/ç-siz. Nəticə icazə və RLS ilə
// (server süzür; menyu yalnız görünən bəndlər).

export type PaletteHit = { kind: 'nav'; section: NavSection } | { kind: 'product'; item: ProductListItem } | { kind: 'customer'; item: CustomerListItem };

export function CommandPalette({ me, sections, onPick, onClose }: { me: MeResponse; sections: NavSection[]; onPick: (h: PaletteHit) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) {
      setProducts([]);
      setCustomers([]);
      return;
    }
    let live = true;
    const h = window.setTimeout(() => {
      if (hasPermission(me, 'product.view')) {
        api
          .products({ q: t, limit: 5 })
          .then((r) => live && setProducts(r.items))
          .catch(() => live && setProducts([]));
      }
      // Müştəri bölməsi menyuda görünürsə (modul açıq, icazə var)
      if (sections.some((x) => x.key === 'customers')) {
        api
          .customers({ q: t, limit: 5 })
          .then((r) => live && setCustomers(r.items))
          .catch(() => live && setCustomers([])); // modul bağlı və ya icazə yoxdur — sadəcə nəticə yoxdur
      }
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(h);
    };
  }, [q]);

  const n = searchNorm(q.trim());
  const navHits: PaletteHit[] = (n ? sections.filter((s) => searchNorm(s.label).includes(n)) : sections).slice(0, 8).map((section) => ({ kind: 'nav', section }));
  const hits: PaletteHit[] = [...navHits, ...products.map((item) => ({ kind: 'product' as const, item })), ...customers.map((item) => ({ kind: 'customer' as const, item }))];
  const label = (h: PaletteHit) => (h.kind === 'nav' ? h.section.label : h.item.name);
  const sub = (h: PaletteHit) => (h.kind === 'nav' ? 'Menyu' : h.kind === 'product' ? 'Mal' : 'Müştəri');

  return (
    <div className="modal-overlay palette-overlay" role="presentation" onClick={onClose}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Axtarış" onClick={(e) => e.stopPropagation()}>
        <input
          ref={input}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowDown') setSel((v) => Math.min(v + 1, Math.max(0, hits.length - 1)));
            else if (e.key === 'ArrowUp') setSel((v) => Math.max(v - 1, 0));
            else if (e.key === 'Enter' && hits[sel]) onPick(hits[sel]);
          }}
          placeholder="Mal, müştəri, menyu axtar…"
          aria-label="Mal, müştəri, menyu axtar"
        />
        <ul className="palette-list" role="listbox" aria-label="Nəticələr">
          {hits.map((h, i) => (
            <li key={`${h.kind}-${h.kind === 'nav' ? h.section.key : h.item.id}`} role="option" aria-selected={i === sel}>
              <button type="button" className={i === sel ? 'is-sel' : ''} onMouseEnter={() => setSel(i)} onClick={() => onPick(h)}>
                <span>{label(h)}</span>
                <span className="muted small">{sub(h)}</span>
              </button>
            </li>
          ))}
          {hits.length === 0 && <li className="muted palette-empty">Heç nə tapılmadı</li>}
        </ul>
      </div>
    </div>
  );
}
